import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {SearchPickerNoResultsRenderer} from "./SearchPickerNoResultsRenderer";
import {DefaultSearchPickerOptions, IPickerItem, ISearcher, SearchPicker} from "searchpicker";

@Component({
  selector: "n-search-picker",
  template: ``,
  encapsulation: ViewEncapsulation.None, // TODO fix
  styleUrls: [
    "./search-picker.css"
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NSearchPickerComponent),
      multi: true
    }
  ]
})
export class NSearchPickerComponent implements ControlValueAccessor, OnChanges, OnInit {

  @Input()
  placeholder: string;

  @Input()
  minlen = 0;

  @Input()
  maxresults: number;

  @Input()
  searcher: ISearcher;

  @Input()
  resultRenderer: (item: IPickerItem, query: string) => Node;

  @Input()
  noResultsRenderer: (query: string) => Text;

  @Input()
  footerRenderer: any;

  @Input()
  isDisabled: boolean;

  @Input()
  isInputDisabled: boolean;

  @Output() pick = new EventEmitter<{query: string, item: any, rank: number, totalCount: number}>();
  @Output() ready = new EventEmitter<SearchPicker>();

  // fields:
  private applying: boolean;
  private searchPicker: SearchPicker;
  private propagateChange: (_: IPickerItem[]) => void;

  constructor(private el: ElementRef,
              private noResultsRendererFactory: SearchPickerNoResultsRenderer) {
    this.el.nativeElement.classList.add("search-picker");
  }

  focus() {
    this.searchPicker.focus();
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes["maxresults"] && !changes["maxresults"].isFirstChange()
      && changes["maxresults"].currentValue !== null
      && changes["maxresults"].currentValue !== undefined) {
      this.searchPicker.options.maxSelectedChoices = changes["maxresults"].currentValue;
    }

    if (changes["isDisabled"] && !changes["isDisabled"].isFirstChange()) {
      this.setDisabled(this.isDisabled);
    }


    if (changes["isInputDisabled"] && !changes["isInputDisabled"].isFirstChange()) {
      if (this.isInputDisabled) {
        this.searchPicker.forbidInput();
      } else {
        this.searchPicker.allowInput();
      }
    }

    if (changes["placeholder"] && !changes["placeholder"].isFirstChange()) {
       this.searchPicker.choices.updatePlaceholder(changes["placeholder"].currentValue);
    }
  }

  ngOnInit() {

    const options = new DefaultSearchPickerOptions();

    options.source = [];
    options.placeholder = this.placeholder || "";
    options.minLengthToSearch = this.minlen;
    options.maxSelectedChoices = this.maxresults;
    options.searcher = this.searcher;
    options.resultRenderer = this.resultRenderer;
    options.noResultsRenderer = this.noResultsRenderer || this.noResultsRendererFactory.createRenderer();
    options.resultFooterRenderer = this.footerRenderer;

    this.searchPicker = new SearchPicker(this.el.nativeElement, options);
    this.searchPicker.on("choiceAdded", (item, query, selectedVia, rank, totalCount) => {
      if (this.applying) {
        return;
      }
      if (this.propagateChange) {
        this.propagateChange(this.searchPicker.getChoices().slice(0));
      }
      this.pick.emit({query, item, rank, totalCount});
    });
    this.searchPicker.on("choiceRemoved", () => {
      if (this.applying) {
        return;
      }
      if (this.propagateChange) {
        this.propagateChange(this.searchPicker.getChoices().slice(0));
      }
    });

    if (this.isDisabled) {
      this.setDisabled(this.isDisabled);
    }

    if (this.isInputDisabled) {
      this.searchPicker.forbidInput();
    }

    this.ready.emit(this.searchPicker);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  writeValue(newArray: IPickerItem[]): void {
    newArray = newArray || [];
    this.applying = true;
    this.searchPicker.clearChoices();
    for (let i = 0; i < newArray.length; i++) {
      this.searchPicker.addChoice(newArray[i]);
    }
    this.applying = false;
  }

  private setDisabled(isDisabled: boolean) {
    if (isDisabled) {
      this.el.nativeElement.style.pointerEvents = "none";
      this.el.nativeElement.style.opacity = 0.5;
    } else {
      this.el.nativeElement.style.pointerEvents = "auto";
      this.el.nativeElement.style.opacity = 1;
    }
  }
}
